/**
 =========================================================
 * Material Dashboard 2 React - v2.2.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import React, { useEffect, useState } from "react";
import { useApi } from "../../context/api.js";
import NOTIFICATIONS_ICONS from "../../assets/icons/notifications-icons.js";
import { formatDate } from "../../utils/date.js";
import useNotifications from "../../context/notifications-actions.js";
import DeleteIcon from "@mui/icons-material/Delete.js";
import Fab from "@mui/material/Fab";

const stopPropagation = (e) => e.stopPropagation();

const columns = [
  {
    Header: (row) => (
      <Checkbox
        checked={row.page.reduce((res, n) => res && n.original.checked, true)}
        indeterminate={
          !!row.page.find((n) => n.original.checked) && !!row.page.find((n) => !n.original.checked)
        }
        onChange={(e) =>
          row.page[0].original.togglePageSelection(
            row.page.map((notif) => notif.original.id),
            e.target.checked
          )
        }
      />
    ),
    accessor: "checked",
    width: "40px",
    align: "center",
    sx: { paddingLeft: 0, paddingRight: 0 },
    Cell: (row) => (
      <Checkbox
        checked={row.value || false}
        onChange={row.row.original.toggleSelection}
        onClick={stopPropagation}
      />
    ),
  },
  {
    accessor: "icon",
    align: "center",
    width: "40px",
    sx: { paddingLeft: 0, paddingRight: 0 },
    Cell: (row) => {
      const Icon = row.value;

      return <Icon sx={{ width: 24, height: 24, marginTop: "4px" }} />;
    },
  },
  {
    Header: "Sujet",
    accessor: "subject",
    align: "left",
  },
  {
    Header: "Date",
    accessor: "date",
    align: "right",
    Cell: (row) => (
      <Tooltip title={formatDate(row.row.original.created, "dd/MM/yyyy hh:mm")}>
        <span>{row.value}</span>
      </Tooltip>
    ),
  },
];

const row = {
  sx: (row) => ({
    fontWeight: row.original.viewed ? "normal" : "bold",
    cursor: row.original.hasAction ? "pointer" : "default",
    backgroundColor: row.original.checked ? "#b3dbfd" : "transparent",
    "&:hover": { backgroundColor: row.original.checked ? "#b3dbfd" : "rgba(0, 0, 0, 0.05)" },
    "& td": {
      paddingTop: 0.5,
      paddingBottom: 0.5,
    },
  }),
};

const setNotifications = (setter, api, notificationActions) => (res) => {
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const togglePageSelection = (page, checked) =>
    setter((notifs) =>
      notifs.map((notif) =>
        page && page.includes(notif.id) ? { ...notif, checked: checked } : notif
      )
    );
  setter(
    res.map((n) => ({
      ...n,
      date: formatDate(n.created, new Date(n.created) > startOfDay ? "hh:mm" : "dd MMM"),
      checked: false,
      togglePageSelection,
      toggleSelection: () =>
        setter((notifs) =>
          notifs.map((notif) => (notif.id === n.id ? { ...notif, checked: !notif.checked } : notif))
        ),
      icon: NOTIFICATIONS_ICONS[n.type] || NOTIFICATIONS_ICONS["other"],
      hasAction: !n.viewed || !!notificationActions[n.type],
      onClick: () => {
        if (!n.viewed) {
          api.markAsViewed(n.id).then((res) => {
            if (res) {
              setter((notifs) =>
                notifs.map((notif) =>
                  notif.id === n.id
                    ? { ...notif, viewed: true, hasAction: !!notificationActions[n.type] }
                    : notif
                )
              );
            }
          });
        }

        if (notificationActions[n.type]) {
          notificationActions[n.type](n.params);
        }
      },
    }))
  );
};

function Notifications() {
  const [unreadNotifications, setUnreadNotifications] = useState(null);
  const [readNotifications, setReadNotifications] = useState(null);

  const api = useApi();
  const notificationActions = useNotifications();

  useEffect(() => {
    // Load notifications
    api
      .getNotificationsUnread()
      .then(setNotifications(setUnreadNotifications, api, notificationActions));
    api
      .getNotificationsRead()
      .then(setNotifications(setReadNotifications, api, notificationActions));
  }, []);

  const handleRemoveNotifications = (notifs, setter) => () => {
    const toRemove = notifs.filter((notif) => notif.checked).map((notif) => notif.id);
    if (toRemove.length) {
      api.removeNotifications(toRemove).then((res) => {
        if (res) {
          setter((notifs) => notifs.filter((notif) => !toRemove.includes(notif.id)));
        }
      });
    }
  };

  return (
    <DashboardLayout>
      {/* TODO : Implement navbar search */}
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white" sx={{ flex: 1 }}>
                  Non lues
                </MDTypography>
                <Fab
                  size="small"
                  color="error"
                  disabled={!unreadNotifications || !unreadNotifications.find((n) => n.checked)}
                  onClick={handleRemoveNotifications(unreadNotifications, setUnreadNotifications)}
                >
                  <DeleteIcon sx={{ height: "24px", width: "24px" }} />
                </Fab>
              </MDBox>
              <MDBox pt={3}>
                {!unreadNotifications && (
                  <MDBox sx={{ textAlign: "center" }}>
                    <CircularProgress color="info" />
                  </MDBox>
                )}
                {!!unreadNotifications && !unreadNotifications.length && (
                  <MDBox sx={{ textAlign: "center" }} mb={2}>
                    <MDTypography variant="caption">Pas de notifications non lues.</MDTypography>
                  </MDBox>
                )}
                {!!unreadNotifications && !!unreadNotifications.length && (
                  <DataTable
                    table={{ columns, rows: unreadNotifications }}
                    row={row}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white" sx={{ flex: 1 }}>
                  Autres notifications
                </MDTypography>
                <Fab
                  size="small"
                  color="error"
                  disabled={!readNotifications || !readNotifications.find((n) => n.checked)}
                  onClick={handleRemoveNotifications(readNotifications, setReadNotifications)}
                >
                  <DeleteIcon sx={{ height: "24px", width: "24px" }} />
                </Fab>
              </MDBox>
              <MDBox pt={3}>
                {!readNotifications && (
                  <MDBox sx={{ textAlign: "center" }}>
                    <CircularProgress color="info" />
                  </MDBox>
                )}
                {!!readNotifications && !readNotifications.length && (
                  <MDBox sx={{ textAlign: "center" }} mb={2}>
                    <MDTypography variant="caption">Pas de notifications.</MDTypography>
                  </MDBox>
                )}
                {!!readNotifications && !!readNotifications.length && (
                  <DataTable
                    table={{ columns, rows: readNotifications }}
                    row={row}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Notifications;
